import Moment from 'moment';

export default class Format {
  /**
   * Utility function that converts a date (in YYYY-MM-DDThh:mm:ss+00:00 format)
   * to MM/DD/YYYY h:mm AM/PM (or DD/MM/YYYY h:mm AM/PM depending on account
   * settings)
   *
   * @param {String} dateTime The unformatted date
   * @param {String} dateTimeFormat The Date Format in the account -
   *     either m/d/Y g:i a  or d/m/Y g:i a
   *
   * @return {String} The formatted date
   */
  static formatDateTime(dateTime, dateTimeFormat) {
    let momentDateTimeFormat = 'MM/DD';
    if (dateTimeFormat.toLowerCase().includes('d/m/y')) {
      momentDateTimeFormat = 'DD/MM';
    }

    momentDateTimeFormat += '/YYYY h:mm A';

    return Moment(dateTime).format(momentDateTimeFormat).toString();
  }

  /**
   * Appends the correct currency symbol to a currency amount
   * and formats the amount
   *
   * @param amount
   * @returns {string} Currency Symbol followed by the Amount
   */
  static formatPrice(amount) {
    let ret = '';
    const floatAmount = parseFloat(amount);

    if (floatAmount < 0) {
      ret += '-';
    }

    ret += window.merchantos.locale.currency_symbol;
    ret += Math.abs(floatAmount).toFixed(2);

    return ret;
  }

  static toMoney(
    value,
    // eslint-disable-next-line camelcase
    currency = window?.merchantos?.locale?.currency_code ?? 'USD',
    localeCode = window?.merchantos?.locale?.lang ?? 'en-US'
  ) {
    const numberToCurrency = new Intl.NumberFormat(localeCode, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    const price = typeof value === 'string' ? parseFloat(value) : value;

    return numberToCurrency.format(price);
  }

  /**
   * Locale-aware short date, date-only (e.g. "Feb 12, 2026"). Native Intl,
   * no moment. Accepts an ISO string, a Y-m-d, or a Date; empty string for a
   * missing value.
   *
   * @param {string|Date|null} value
   * @param {string} localeCode BCP-47 tag; defaults to the merchant locale
   * @returns {string}
   */
  static formatDate(
    value,
    // eslint-disable-next-line camelcase
    localeCode = window?.merchantos?.locale?.lang ?? 'en-US'
  ) {
    if (!value) {
      return '';
    }

    // A date-only string ('YYYY-MM-DD') parses as UTC midnight, which Intl then
    // renders in the local zone — a day early in negative-offset zones. Pin it to
    // local midnight so a calendar date renders as that date everywhere; datetimes
    // and Date objects are left untouched.
    const normalized =
      typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : value;
    const date = new Date(normalized);
    // Intl throws a RangeError on an invalid date; degrade like a missing value.
    if (Number.isNaN(date.getTime())) {
      return '';
    }

    return new Intl.DateTimeFormat(localeCode, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(date);
  }
}
